import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import ManageServiceForm from './ManageServiceForm';
import { useAxios, APIError } from '../../core/_axios';
import { Service } from '../../core/_models';
import { getServiceByUniqueIdentifier } from '../../core/_requests_v2';
import usePageTitle from '../../hooks/usePageTitle';
import EndServiceForm from './EndServiceForm';
import StartServiceForm from './StartServiceForm';
import ServiceQRCode from './ServiceQRCode';
import { SignaturesList } from '../../components/SignaturesList';
import { MemoriesList } from '../../components/MemoriesList';
import { useAuth } from '../../AuthContext';
import SendServiceToIpad from './SendServiceToIpad';
import SendServiceToFamily from './SendServiceToFamily';
import AddFamilyMemberToServiceForm from './AddFamilyMemberToServiceForm';
import { CONTAINER_CONTENT_CLASS } from '../signatures/Signatures';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

export function ManageServiceRoutes() {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const {
        pathname
    } = useLocation();

    const {
        viewingAllAccounts
    } = useAuth();

    const params = useParams();

    usePageTitle('Manage Service');

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [service, setService] = useState<Service | null>(null);
    const [signatureCount, setSignatureCount] = useState<number | null>(null);
    const [memoryCount, setMemoryCount] = useState<number | null>(null);

    const loadService = useCallback(async () => {
        if (!params.serviceUniqueId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadingError(null);
        try {
            const updatedService = await getServiceByUniqueIdentifier(axios, params.serviceUniqueId);
            setService(updatedService);
            
            // Fetch signature count
            try {
                const sigResponse = await axios.get(`/api/v1/app/authenticated/services/${params.serviceUniqueId}/signatures`);
                if (sigResponse.data && sigResponse.data.data) {
                    // The API returns {data: {signatures: [...], signatures_count: number}}
                    const sigData = sigResponse.data.data;
                    setSignatureCount(sigData.signatures_count || sigData.signatures?.length || 0);
                }
            } catch (err) {
                console.error('Failed to fetch signature count:', err);
                setSignatureCount(0);
            }
            
            // Fetch memory count
            try {
                const memResponse = await axios.get(`/api/v1/app/authenticated/services/${params.serviceUniqueId}/memories`);
                if (memResponse.data && memResponse.data.data) {
                    setMemoryCount(memResponse.data.data.total_count);
                }
            } catch (err) {
                console.error('Failed to fetch memory count:', err);
                setMemoryCount(0);
            }
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, params.serviceUniqueId]);

    useEffect(() => {
        loadService();
    }, [loadService]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadService}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return <>
        <div className="container-fixed">
            <div className={CONTAINER_CONTENT_CLASS}>
                <div className="flex flex-col justify-center gap-2">
                    <h1 className="text-xl font-semibold leading-none text-gray-900 flex items-center gap-2">
                        {service.public_data.deceased_person.full_name ? <>
                            {viewingAllAccounts ? `Manage ${service.public_data.deceased_person.full_name}'s Service (${service.account.name})` : `Manage ${service.public_data.deceased_person.full_name}'s Service`}
                        </> : <>
                            {!viewingAllAccounts ? `Manage Service (${service.account.name})` : `Manage Service`}
                        </>}
                        {service.public_data.current_language === 'es-ES' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {copy.Common.labels.languageSpanish}
                            </span>
                        )}
                    </h1>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                        {/* subtitle? */}
                    </div>
                </div>
                {false && <div className="flex items-center gap-2.5">
                    <a className="btn btn-sm btn-light" href="#">
                        Go to Teams
                    </a>
                </div>}
            </div>
        </div >
        <div className="container-fixed">
            <div className='mb-5'>
                <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
                    <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}`}
                            className={[
                                pathname === `/services/${service.unique_identifier}/details` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Details
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}/signatures`}
                            className={[
                                pathname === `/services/${service.unique_identifier}/signatures` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Signatures {signatureCount !== null && (
                                <span className="ml-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                                    {signatureCount}
                                </span>
                            )}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}/memories`}
                            className={[
                                pathname === `/services/${service.unique_identifier}/memories` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Memories {memoryCount !== null && (
                                <span className="ml-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                                    {memoryCount}
                                </span>
                            )}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}/service-qr-code`}
                            className={[
                                pathname === `/services/${service.unique_identifier}/service-qr-code` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Service QR Code
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}/send-service-to-ipad`}
                            className={[
                                pathname === `/services/${service.unique_identifier}/send-service-to-ipad` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Send Service to iPad
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}/send-service-to-family`}
                            className={[
                                pathname.startsWith(`/services/${service.unique_identifier}/send-service-to-family`) || pathname.startsWith(`/services/${service.unique_identifier}/add-family-member`) ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Send Service to Family
                        </Link>
                    </li>
                    {service?.is_active ? <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}/end`}
                            className={[
                                pathname === `/services/${service.unique_identifier}/end` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            End Service
                        </Link>
                    </li> : <li className="me-2">
                        <Link to={`/services/${service.unique_identifier}/start`}
                            className={[
                                pathname === `/services/${service.unique_identifier}/start` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Start Service
                        </Link>
                    </li>}
                </ul>
            </div>
            <Routes>
                <Route path="/details" element={
                    <ManageServiceForm
                        service={service}
                        onSetService={setService}
                    />}
                />
                <Route path="/signatures" element={
                    <SignaturesList
                        service={service}
                    />}
                />
                <Route path="/memories" element={
                    <MemoriesList
                        service={service}
                    />}
                />
                <Route path="/service-qr-code" element={
                    <ServiceQRCode
                        service={service}
                    />}
                />
                <Route path="/send-service-to-ipad" element={
                    <SendServiceToIpad
                        service={service}
                    />}
                />
                <Route path="/send-service-to-family" element={
                    <SendServiceToFamily
                        service={service}
                    />}
                />
                <Route path="/add-family-member" element={
                    <AddFamilyMemberToServiceForm
                        service={service}
                    />}
                />
                <Route path="/start" element={
                    <StartServiceForm
                        service={service}
                        onSetService={setService}
                    />}
                />
                <Route path="/end" element={
                    <EndServiceForm
                        service={service}
                        onSetService={setService}
                    />}
                />
                <Route
                    path="*"
                    element={<Navigate
                        to={`/services/${service.unique_identifier}/details`}
                        replace
                    />}
                />
            </Routes>
        </div>
    </>
}